import { Accordion, Badge, Button, ProgressBar } from 'react-bootstrap';
import { ColorSetting, WootMappedIAGDetails, OrdersWootTableRow, WootAllocationExcelRow } from '../../WootOrdersImport.interfaces';
import { apiFetch } from '../../../utility/fetch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserData } from '../../../user/UserContext';
import { useLoading } from '../../../loading/LoadingContext';
import { getSalesforceUrlByWindowLocation } from '../../../common/common.entities';
import CustomError, { CustomErrorProps } from '../../../common/components/CustomError';
import { DateTime } from 'luxon';
import { Inventory__c } from '../../../common/common.interfaces';
import { mapWootAllocationIAGs } from '../../woot.fba.helpers';
import WootFbaJourneyStep from './WootFbaJourneyStep';
import { ContentItem, SalesforceCreateParentChildRequestBody } from '../../../common/common.api.interfaces';

interface WootFbaJourneyAllocationProps {
  row: OrdersWootTableRow
  allocationColors: ColorSetting
  handleMapAllocationIag: any
  handleDownloadFile: any
  handlePreviewFile: any
  setDataError: any
  setLastRefresh: any
  wootOrders: OrdersWootTableRow[]
  userData: UserData
}

export default function WootFbaJourneyAllocation(props: WootFbaJourneyAllocationProps) {
  const [createInventoryDisabled, setCreateInventoryDisabled] = useState(false)
  const {changeStep, changeSubStep, toggleLoading} = useLoading()
  const [allocationIag, setAllocationIag]: [WootMappedIAGDetails, any] = useState(undefined)
  const [allocationError, setAllocationError]: [CustomErrorProps, any] = useState(undefined)
  const [inventoryError, setInventoryError]: [CustomErrorProps, any] = useState(undefined)

  const WOOT_FBA_CB_PATH = 'crystal-ball/woot/fba'

  useEffect(() => {
    /** One time load the allocation iag if it exists */
    const load = async function () {
      if(!props.row.allocationIagMappedPath){
        return
      }
      changeStep('Loading Mapped Allocation IAG')
      changeSubStep(`Loading ${props.row.allocationIagMappedPath}`)
      toggleLoading(true)
      const response = await apiFetch({
        endpoint: 'blob',
        method: 'GET',
        accessToken: props.userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: props.row.allocationIagMappedPath}
        ]
      })
      setAllocationIag(response.body)
      setInventoryError({
        closeError: undefined,
        content: <div>
          {(response?.body as any)?.createInventory?.map(i => (
            <span title={`${i.Name}`} style={{padding: '2px', cursor: 'pointer', fontSize: '1.5em'}}><Badge bg='secondary'>{i.Name}</Badge>&nbsp;</span>
          ))}
        </div>,
        description: 'Some inventories were not found in Mission Control. Please review them, click "CREATE INVENTORIES", and then click "MAP IA GROUPS" to resolve.',
        title: 'Missing Mission Control WOOT FBA Inventory',
        visible: props.row.hasAllocationMappingErrors === true
      })
      changeStep('Loading Mapped Allocation IAG')
      toggleLoading(false)
    }
    load()
  }, [props.row])

  // #region MAP IAG
  const handleMapAllocationIag = useCallback(async (order: OrdersWootTableRow) => {
    props.setDataError(undefined)
    changeStep('Requesting Mission Control Data')
    changeSubStep('Loading Product Data')
    toggleLoading(true)
    try {
      // Load Souce JSON Converted
      const allocationJsonResponse = await apiFetch<WootAllocationExcelRow[]>({
        endpoint: 'blob',
        method: 'GET',
        accessToken: props.userData.accessToken,
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: order.sourcePoJsonPath},
          {key: 'json', value: 'true'}
        ]
      })
      if(!allocationJsonResponse.ok){
        props.setDataError({
          content: JSON.stringify(allocationJsonResponse.body),
          description: `Failed to get Source PO JSON. rowKey[${order.rowKey}]`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Determining SKU/ASIN Uniques for Product and Inventory Queries')
      const uniqueSkus = []
      const uniqueASIN = []
      for(const row of allocationJsonResponse.body){
        const sku = row.ModelNumber?.trim().toLowerCase()
        const asin = row.ASIN?.trim()
        if(sku?.length > 0){
          if(!uniqueSkus.includes(sku)){
            uniqueSkus.push(sku)
          }
        }
        if(asin?.length > 0){
          if(!uniqueASIN.includes(asin)){
            uniqueASIN.push(asin)
          } 
        }
      }

      changeSubStep('Getting Product Inventory')
      const mpnIn = `'${uniqueSkus.join(`','`)}'`
      const asinIn = `'${uniqueASIN.join(`','`)}'`
      let asinQuery = ''
      if(uniqueASIN.length > 0){
        asinQuery = ` OR Product__r.ASIN__c IN (${asinIn})`
      }
      const soql = `SELECT Id, Name, Fulfillable_Inventory__c, Product__c, Product__r.MPN__c, Product__r.ASIN__c, Warehouse__r.Name FROM Inventory__c WHERE (Product__r.MPN__c IN (${mpnIn})${asinQuery}) AND Warehouse__r.Name IN ('FBA Woot', 'FBA US') AND Product__r.RecordType.Name = 'Product'`
      const inventoryResponse = await apiFetch<{records: Inventory__c[]}>({
        endpoint: 'salesforceQuery',
        method: 'POST',
        accessToken: props.userData.accessToken,
        body: JSON.stringify({
          soql
        })
      })
      if(!inventoryResponse.ok){
        props.setDataError({
          content: JSON.stringify(inventoryResponse.body),
          description: `Failed to get Inventory from Mission Control.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Mapping IAGs')
      const mapped = mapWootAllocationIAGs(order, allocationJsonResponse.body, inventoryResponse.body.records)
      
      changeSubStep('Saving Mapping Results')
      const saveResultBlobName = `${WOOT_FBA_CB_PATH}/${order.poNumber}/${order.poNumber}-allocation-mapped.json`
      const saveResultResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: props.userData.accessToken,
        body: JSON.stringify(mapped),
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: saveResultBlobName}
        ]
      })
      if(!saveResultResponse.ok){
        props.setDataError({
          content: JSON.stringify(saveResultResponse.body),
          description: `Failed to save IAG mapped results file.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      changeSubStep('Saving Journey')
      //only modify the field in question in case other are modifying it
      const saveJourneyResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'POST',
        accessToken: props.userData.accessToken,
        body: JSON.stringify({
          table: 'ordersWoot',
          records: [
            {
              partitionKey: order.partitionKey, 
              rowKey: order.rowKey, 
              allocationIagMappedPath: saveResultBlobName, 
              hasAllocationMappingErrors: mapped.validation.length > 0
            }
          ]
        })
      })
      if(!saveJourneyResponse.ok){
        props.setDataError({
          content: JSON.stringify(saveJourneyResponse.body),
          description: `Failed to save IAG mapped results record.`,
          title: 'Allocation IAG Mapping Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      props.setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))
    }catch(error){
      props.setDataError({
        content: JSON.stringify(error),
        description: `Failed to map allocation IAG. ${JSON.stringify(error)}`,
        title: 'Allocation IAG Mapping Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
  }, [props.wootOrders, props.userData])

  // #region CREATE INVENTORIES
  const handleCreateInventories = useCallback(async (inventories: Inventory__c[], row: OrdersWootTableRow, doRefresh?: boolean) => {
    if(!confirm(`The following inventories will be created: [ ${inventories.map(i => i.Name).join(', ')} ]`)){
      return
    }
    setAllocationError(undefined)
    changeStep(`Creating Mission Control ${inventories.length} Inventories`)
    changeSubStep(`Sending data to Mission Control to create inventories now.`)
    toggleLoading(true)
    const response = await apiFetch({
      endpoint: 'salesforceInventory',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(inventories)
    })

    if(!response.ok){
      setAllocationError({
        content: JSON.stringify(response.body),
        description: `Failed to create Mission Control Inventories.`,
        title: 'Inventory Create Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    } else {
      /** Save the response to blob and store the path on the record */
      const responsePath = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-inventory-create-response.json`
      const saveResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: responsePath}
        ],
        accessToken: props.userData.accessToken,
        body: JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          body: response.body
        })
      })
      if(!saveResponse.ok){
        setAllocationError({
          content: JSON.stringify(saveResponse.body),
          description: `Failed to save Mission Control Inventories response.`,
          title: 'Inventory Create Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }

      const updateResponse = await apiFetch({
        endpoint: 'azureTables',
        method: 'POST',
        accessToken: props.userData.accessToken,
        body: JSON.stringify({
          table: 'ordersWoot',
          records: [{
            partitionKey: row.partitionKey,
            rowKey: row.rowKey,
            allocationInventoryResponsePath: responsePath,
            hasAllocationMappingErrors: false
          }]
        })
      })
      if(!updateResponse.ok){
        setAllocationError({
          content: JSON.stringify(updateResponse.body),
          description: `Failed to update journey with inventory response path.`,
          title: 'Inventory Create Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
    }
    const newRowResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: props.userData.accessToken,
      params: [
        {key: 'table', value: 'ordersWoot'},
        {key: 'filter', value: `PartitionKey eq '${row.partitionKey}' and RowKey eq '${row.rowKey}'`}
      ]
    })
    if(!newRowResponse.ok){
      setAllocationError({
        content: JSON.stringify(newRowResponse.body),
        description: `Failed to get journey with after inventory created.`,
        title: 'Inventory Create Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    await handleMapAllocationIag((newRowResponse.body as OrdersWootTableRow[])[0])
    toggleLoading(false)
  }, [props.userData])

  // #region CREATE IAG
  const handleAllocationIagCreate = useCallback(async (content: WootMappedIAGDetails, row: OrdersWootTableRow) => {
    setAllocationError(undefined)
    changeStep(`Creating Mission Control Inventory Adjustment Groups`)
    toggleLoading(true)
    
    changeSubStep(`Sending data to Mission Control to create IAGs now.`)
    const contents: ContentItem[] = []
    for(const iagContent of content.iagContent){
      contents.push({
        parent: iagContent.iag,
        children: iagContent.ias
      })
    }
    const requestBody: SalesforceCreateParentChildRequestBody = {
      content: contents,
      childObjectName: 'Inventory_Adjustment__c',
      childObjectParentIdField: 'Inventory_Adjustment_Group__c',
      parentObjectName: 'Inventory_Adjustment_Group__c'
    }
    const response = await apiFetch({
      endpoint: 'salesforceCreateParentChild',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(requestBody)
    })
    if(!response.ok){
      setAllocationError({
        content: JSON.stringify(response.body),
        description: `Failed to create Allocation Inventory Adjustment Groups.`,
        title: 'Allocation IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving response data from Mission Control.`)
    const responsePath = `${WOOT_FBA_CB_PATH}/${row.poNumber}/${row.poNumber}-iag-responses.json`
    const saveResponse = await apiFetch({
      endpoint: 'blob',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify(response.body),
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'storage'},
        {key: 'blobname', value: responsePath}
      ],
    })
    if(!saveResponse.ok){
      setAllocationError({
        content: JSON.stringify(saveResponse.body),
        description: `Failed save Allocation Inventory Adjustment Groups response.`,
        title: 'Allocation IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving Journey`)
    const updateResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          allocationIagResponsePath: responsePath,
          allocationStatus: 'complete',
          returnStatus: 'pending'
        }]
      })
    })
    if(!updateResponse.ok){
      setAllocationError({
        content: JSON.stringify(updateResponse.body),
        description: `Failed update Journey record.`,
        title: 'Allocation IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Gettings IAGs`)
    const iagResponse = await apiFetch<any>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        soql: `SELECT Id, Shipment_Name__c, Status__c FROM Inventory_Adjustment_Group__c WHERE Shipment_Name__c LIKE 'Woot FBA Flip Allocation ${row.poNumber}%'`
      })
    })
    if(!iagResponse.ok){
      setAllocationError({
        content: JSON.stringify(iagResponse.body),
        description: `Failed getting IAGs from Mission Control.`,
        title: 'Allocation IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    changeSubStep(`Saving Journey`)
    const allocationIags = iagResponse.body.records.map(r => {return {Id: r.Id, Shipment_Name__c: r.Shipment_Name__c, Status__c: r.Status__c}})

    const updateResponse2 = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: props.userData.accessToken,
      body: JSON.stringify({
        table: 'ordersWoot',
        records: [{
          partitionKey: row.partitionKey,
          rowKey: row.rowKey,
          allocationIags: JSON.stringify(allocationIags),
          allocationStatus: allocationIags.length > 0 ? 'complete' : 'pending',
          returnStatus: allocationIags.length > 0 ? 'pending' : undefined
        }]
      })
    })
    if(!updateResponse2.ok){
      setAllocationError({
        content: JSON.stringify(updateResponse2.body),
        description: `Failed update Journey record.`,
        title: 'Allocation IAG Saving Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }

    props.setLastRefresh(DateTime.now().setZone('America/Chicago').toLocaleString(DateTime.TIME_WITH_SECONDS))

    toggleLoading(false)
  }, [props.userData])

  const handleCloseAllocationError = useCallback((e) => {
    setAllocationError({
      visible: false
    })
  }, [])

  // #region VIEW
  const view = useMemo(() => {
    const {row, allocationColors} = props

    const steps = []

    let complete = 0
    let pending = 0
    let stepsMax = 2
    if(row.hasAllocationMappingErrors === true && !row.allocationInventoryResponsePath){
      steps.push('CREATE INVENTORIES')
      pending += 1
      stepsMax += 1
    } else if(row.hasAllocationMappingErrors === false && row.allocationInventoryResponsePath) {
      complete += 1
      stepsMax += 1
    }

    if(!row.allocationIagMappedPath || row.hasAllocationMappingErrors === true){
      steps.push('MAP IA GROUPS')
      pending += 1
    } else if(row.allocationIagMappedPath) {
      complete += 1
    }
    
    if(!row.allocationIags){
      steps.push('CREATE IA GROUPS')
    } else {
      complete += 1
    }

    const pendingProgress = (pending / stepsMax) * 100
    const completeProgress = (complete / stepsMax) * 100
    let warning = 0
    if(completeProgress < 100){
      warning = 1
    }
    const warningProgress = (warning / stepsMax) * 100
    return <>
    <Accordion defaultActiveKey={['pending', 'error'].includes(row.allocationStatus) ? 'allocation' : null} className='custom-accordion'>
      <Accordion.Item eventKey='allocation'>
        <Accordion.Header className='sticky-row-2'>
          <span className='beacon-header'>ALLOCATION PO IAG</span>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <span><Badge bg={allocationColors.bg} text={allocationColors.fg}>{row.allocationStatus.toUpperCase()}</Badge></span>
        </Accordion.Header>
        <Accordion.Body>
          <CustomError closeError={handleCloseAllocationError} content={allocationError?.content} description={allocationError?.description} title={allocationError?.title} visible={allocationError?.visible} />
          <div>
            <div style={{textAlign: 'center'}}>
              <span className='beacon-label'>Steps {complete}/{stepsMax} Complete</span>
              <ProgressBar>
                <ProgressBar animated variant='success' now={completeProgress}/>
                <ProgressBar animated variant='warning' now={warningProgress} />
                <ProgressBar animated variant='secondary' now={pendingProgress} />
              </ProgressBar>
            </div>
            <div>
              {steps.length > 0 && 
                <div className='beacon-header'>NEXT STEP {
                  steps[0] === 'CREATE INVENTORIES' ? <Button variant='success' onClick={async () => {await handleCreateInventories(allocationIag.createInventory, props.row)}} title='Creates WOOT FBA inventories in Mission Control.'>CREATE INVENTORIES</Button> :
                  steps[0] === 'MAP IA GROUPS' ? <Button variant='success' onClick={async () => {await handleMapAllocationIag(row)}} title='Maps IA Groups and provides feedback on any issues encountered.'>MAP IA GROUPS</Button> :
                  steps[0] === 'CREATE IA GROUPS' ? <Button variant='success' onClick={async () => {await handleAllocationIagCreate({iagContent: allocationIag.iagContent} as any, row)}} title='Creates mapped IA Groups in Mission Control.'>CREATE IA GROUPS</Button> : null
                }</div>
              }
            </div>
            <div>
              <span className='beacon-header'>REQUIREMENTS</span>
              <div style={{display: 'flex', flexWrap: 'wrap'}}>
                <WootFbaJourneyStep
                  title='ALLOCATION PDF'
                  item={row.sourcePoPdfPath}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourcePoPdfPath === 'string'}
                ></WootFbaJourneyStep>
                <WootFbaJourneyStep
                  title='ALLOCATION EXCEL'
                  item={row.sourcePoExcelPath}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={false}
                  isSuccess={typeof row.sourcePoExcelPath === 'string'}
                ></WootFbaJourneyStep>
                <WootFbaJourneyStep
                  title='ALLOCATION JSON'
                  item={row.sourcePoJsonPath}
                  itemRequiresParse={false}
                  handleDownloadFile={props.handleDownloadFile}
                  handlePreviewFile={props.handlePreviewFile}
                  isPreviewable={true}
                  isSuccess={typeof row.sourcePoJsonPath === 'string'}
                ></WootFbaJourneyStep>
              </div>
            </div>
            <span className='beacon-header'>RESULTS</span>
            <div style={{display: 'flex', flexWrap: 'wrap'}}>
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                item={row.allocationInventoryResponsePath ?? 'NOT REQUIRED'}
                isSuccess={
                  row.hasAllocationMappingErrors === false && 
                  ((typeof row.allocationInventoryResponsePath === 'string'
                  && row.allocationInventoryResponsePath.trim() !== '') || 
                  !row?.allocationInventoryResponsePath)
                }
                itemRequiresParse={false}
                title='CREATED INVENTORIES'
              />
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                item={row.allocationIagMappedPath}
                isSuccess={
                  typeof row.allocationIagMappedPath === 'string' &&
                  row.allocationIagMappedPath.trim() !== ''
                }
                itemRequiresParse={false}
                title='MAPPED IA GROUPS'
              />
              <WootFbaJourneyStep 
                handleDownloadFile={props.handleDownloadFile}
                handlePreviewFile={props.handlePreviewFile}
                isPreviewable={true}
                item={row.allocationIagResponsePath}
                isSuccess={typeof row.allocationIagResponsePath === 'string'}
                itemRequiresParse={false}
                title='CREATED IA GROUPS'
              />
            </div>
            {row.allocationIags &&
              <div className='beacon-header'>IA GROUPS</div>
            }
            {row.allocationIags && <div>
                {JSON.parse(row.allocationIags).map(iag => (
                  <div className='journey-title'>
                    <span className='beacon-link'>
                      <a className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${getSalesforceUrlByWindowLocation(window.location.href)}/${iag.Id}`}>{iag.Shipment_Name__c}</a>
                    </span>
                  </div>
                ))}
              </div>
            }
            <CustomError 
              closeError={() => {setInventoryError({visible: false})}}
              content={inventoryError?.content}
              description={inventoryError?.description}
              title={inventoryError?.title}
              visible={inventoryError?.visible}
            />
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
    </>
  }, [props, allocationIag, createInventoryDisabled, inventoryError, allocationError, props.row])

  return view
}